import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { motion } from "framer-motion";
import "./FormStyles.css";

const validationSchema = yup.object().shape({
  name: yup.string().required("Please enter your name."),
  email: yup
    .string()
    .email("The email address entered is invalid. Please provide a valid email.")
    .required("Please enter your email address."),
  subject: yup.string().required("Please enter the subject of your message."),
  message: yup.string().required("Message is required"),
});

const Form = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successData, setSuccessData] = useState(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data) => {
    try {
      setSubmitError(null);
      const apiUrl = "/api/send-email";
      
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccessData({
          name: data.name,
          subject: data.subject
        });
        setIsSubmitted(true);
        reset();
      } else {
        throw new Error(result.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitError(error.message);
    }
  };

  return (
    <div className="Form">
      {!isSubmitted ? (
        <motion.form
          onSubmit={handleSubmit(onSubmit)}
          aria-label="Contact Form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {/* Name Input */}
          <motion.label
            htmlFor="name"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Your Name
          </motion.label>
          <motion.input
            id="name"
            type="text"
            placeholder="Enter your full name"
            {...register("name")}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
          {errors.name && <p className="error">{errors.name.message}</p>}

          {/* Email Input */}
          <motion.label
            htmlFor="email"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Email
          </motion.label>
          <motion.input
            id="email"
            type="email"
            placeholder="Enter your email address"
            {...register("email")}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          />
          {errors.email && <p className="error">{errors.email.message}</p>}

          {/* Subject Input */}
          <motion.label
            htmlFor="subject"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            Subject
          </motion.label>
          <motion.input
            id="subject"
            type="text"
            placeholder="Enter the subject"
            {...register("subject")}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
          />
          {errors.subject && <p className="error">{errors.subject.message}</p>}

          {/* Message Input */}
          <motion.label
            htmlFor="message"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            Message
          </motion.label>
          <motion.textarea
            id="message"
            rows="6"
            placeholder="Type your message here"
            {...register("message")}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
          />

          {/* Submit Button */}
          <motion.button
            className="btn"
            type="submit"
            disabled={isSubmitting}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </motion.button>

          {/* Error Message */}
          {submitError && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              ⚠️ {submitError}
            </motion.div>
          )}
        </motion.form>
      ) : (
        /* Success Message */
        <motion.div
          className="success-message"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="success-header">
            <svg className="checkmark" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <h3>Message Sent Successfully!</h3>
          </div>
          <div className="success-body">
            <p>Thank you, {successData?.name}! I've received your message.</p>
            <p>I'll respond to your inquiry within 24 hours.</p>
            <div className="success-details">
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Form;